import React, { useContext } from "react";

import { GROUPED_BETTING_TYPES } from "@data-types/offer/event-betting-type/BettingTypeGroups";
import { BettingData } from "@data-types/sport-stats/SportStatsTypes";
import { OfferTip } from "@features/offer/desktop/buttons";
import BettingTypeStatistics from "@features/offer/desktop/statistics/BettingTypeStatistics";
import { PlayerStatistics } from "@features/offer/shared/statistics/components/player-betting-types";
import { EventContext, LookupContext, OfferStoreContext } from "@gp/components";
import { EventKey, EventKeyBettingOffer, Player } from "@gp/models";
import { OfferConfigurationStore } from "@gp/offer";
import { NameProvider } from "@gp/utility";
import classNames from "classnames";
import { ObservableMap } from "mobx";
import { observer } from "mobx-react";

import { BettingTypeToolTip } from "../BettingTypeToolTip";
import { BettingTypeName } from "./BettingTypeName";
import { BettingTypeOffer } from "./BettingTypeOffer";

const nameProvider = new NameProvider();

export const bettingTypesVertical = [
	"1st-2nd-half-both-teams-to-score",
	"1st-half-correct-score",
	"1st-half-correct-score-score-max-3",
	"1st-half-exact-number-of-goals",
	"1st-half-exact-number-of-goals-of-away-team",
	"1st-half-exact-number-of-goals-of-home-team",
	"1st-half-tip-1x2-both-teams-to-score",
	"1st-half-tip1x2-over-under",
	"3-way-both-teams-to-score",
	"both-teams-to-score-over-under",
	"correct-score",
	"correct-score-max-4",
	"correct-score-score-max-6",
	"double-chance-total",
	"double-result-1st-set-match",
	"exact-number-of-goals",
	"goalnr-goal-1x2",
	"goals-away-team",
	"goals-home-team",
	"halftime-fulltime",
	"highest-scoring-quarter",
	"quarternr-quarter-team-winning-margin",
	"setnr-set-correct-score",
	"tip-1x2-and-over-under",
	"total-goals-aggregated",
	"winner-total",
	"winner-total-incl-overtime",
	"winning-margin",
	"winning-margin-incl-overtime",
	"which-team-to-score",
	"framenr-frame-highest-break",
	"frames-1-to-framenr-correct-score",
	"setnr-set-correct-score-in-legs",
	"correct-score-in-sets",
	"double-chance-both-teams-to-score",
	"highest-scoring-period",
	"winner-total-incl-extra-innings",
];

export const bettingTypesTwoCols = [
	"1st-2nd-half-both-teams-to-score",
	"double-chance-total",
	"touchdownnr-touchdown-scorer-incl-overtime",
	"quarternr-quarter-team-winning-margin",
	"winner-total-incl-overtime",
];

export const offerInline = [
	"winning-margin-incl-overtime",
	"winning-margin-incl-overtime-and-penalties",
	"winning-margin-incl-extra-innings",
	"penalty-shootout-winning-margin",
	"any-team-winning-margin-incl-overtime",
	"quarternr-quarter-team-winning-margin",
	"any-team-performance-winning-margin",
	"winning-margin",
	"quarternr-quarter-winning-margin",
	"1st-half-winning-margin",
	"gamenr-game-winning-margin",
];

export const BettingTypeRow = observer(function BettingTypeRow(props: {
	bettingType: Parameters<typeof BettingTypeName>[0]["bettingType"] &
	Parameters<typeof BettingTypeOffer>[0]["bettingType"] & {
		nameForOfferList: string;
		description: string;
		abrv: string;
	};
	offerKey: EventKey;
	index: number;
	bettingTypeStats: BettingData[] | null;
	setBettingTypeStats: React.Dispatch<
		React.SetStateAction<BettingData[] | null>
	>;
	additionalClassName?: string;
}) {
	const { bettingType, offerKey, additionalClassName } = props;

	const event = useContext(EventContext);
	const { teams } = useContext(LookupContext);
	const offerStore = useContext(OfferStoreContext);

	const homeTeam = teams.get(event.teamOneId || "");
	const awayTeam = teams.get(event.teamTwoId || "");

	const specifiers = {
		...offerKey.specifier,
		competitor1: homeTeam?.name || "",
		competitor2: awayTeam?.name || "",
	};
	let shouldDisplayToolTip = true;
	let name = "";

	const isMarginalBettingTypeWithGroupedName = GROUPED_BETTING_TYPES.includes(
		bettingType.abrv
	);

	const isSpecialBettingTypeWithGroupedName = [
		"player-total-assists",
		"player-total-rebounds",
		"player-total-3-point-field-goals",
		"player-total-points",
	].includes(bettingType.abrv);

	if (!isMarginalBettingTypeWithGroupedName) {
		name = nameProvider.getName(bettingType.nameForOfferList, specifiers);
	} else {
		//@ts-expect-error postoji
		name = offerKey?.specifier["hcp"] || offerKey.specifier["total"];
		shouldDisplayToolTip = false;
	}

	const description = nameProvider.getName(
		bettingType.description,
		specifiers
	);

	const hasStatsButton = //@ts-expect-error
		offerStore.sportStatisticsStore.availableBTypesSet.has(
			bettingType.abrv
		) &&
		shouldDisplayToolTip &&
		props.index == 0;

	const offerItemClasses = classNames(
		"offer--additional__row",
		{
			"offer--additional__row--full":
				!bettingTypesVertical.includes(bettingType.abrv) &&
				!hasStatsButton &&
				!offerInline.includes(bettingType.abrv),
			"offer--additional__row--grouped":
				isMarginalBettingTypeWithGroupedName ||
				isSpecialBettingTypeWithGroupedName,
		},
		additionalClassName
	);

	return (
		<>
			<div className={offerItemClasses} data-type={bettingType.abrv}>
				<BettingTypeName
					name={name}
					bettingType={bettingType}
					offerKey={props.offerKey}
					setBettingTypeStats={props.setBettingTypeStats}
					bettingTypeStats={props.bettingTypeStats}
					index={props.index}
					shouldDisplayToolTip={shouldDisplayToolTip}
				/>

				{shouldDisplayToolTip && props.index == 0 && (
					<BettingTypeStatistics
						bettingTypeAbrv={bettingType.abrv}
						bettingTypeStats={props.bettingTypeStats}
					/>
				)}

				<BettingTypeOffer
					bettingTypeStats={props.bettingTypeStats}
					bettingType={bettingType}
					offerKey={offerKey}
				/>
				<BettingTypeToolTip name={name} description={description} />
			</div>
		</>
	);
});
